import React, { useState } from 'react';
import { pursuitApi } from '../../api/client';

// Seller pursuit: Self-Score. Runs an AI color-team review of the drafted proposal
// against the compliance matrix + evaluation factors before submission. Advisory.

interface Props { oppId: string; }

const READINESS: Record<string, { bg: string; fg: string; label: string }> = {
  ready: { bg: '#dcfce7', fg: '#166534', label: 'READY' },
  needs_work: { bg: '#fef9c3', fg: '#854d0e', label: 'NEEDS WORK' },
  not_ready: { bg: '#fee2e2', fg: '#991b1b', label: 'NOT READY' },
};
const FACTOR: Record<string, { bg: string; fg: string }> = {
  addressed: { bg: '#dcfce7', fg: '#166534' },
  partial: { bg: '#fef9c3', fg: '#854d0e' },
  missing: { bg: '#fee2e2', fg: '#991b1b' },
};

export default function SelfScoreTab({ oppId }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [score, setScore] = useState<any>(null);

  const run = async () => {
    setLoading(true); setError(''); setScore(null);
    try {
      const res = await pursuitApi.selfScore(oppId);
      setScore(res.self_score);
    } catch (e: any) {
      setError(e?.message || 'Self-score failed. Draft a proposal volume first (Proposal Response).');
    }
    setLoading(false);
  };

  const r = score?.readiness ? (READINESS[score.readiness] || READINESS.needs_work) : null;
  const cov = score?.coverage;

  return (
    <div style={{ maxWidth: 920 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, margin: 0 }}>Self-Score / Readiness</h2>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0', maxWidth: 640 }}>
            An AI color-team review of your drafted proposal against the compliance requirements and
            evaluation factors — before you submit. Advisory; use it to close gaps.
          </p>
        </div>
        <button onClick={run} disabled={loading}
          style={{ padding: '9px 16px', background: loading ? '#93c5fd' : '#4338ca', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
          {loading ? 'Scoring…' : score ? 'Re-score' : 'Run Self-Score'}
        </button>
      </div>

      {error && <div role="alert" style={{ color: '#991b1b', background: '#fee2e2', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{error}</div>}

      {!score ? (
        !error && <div style={{ padding: 24, border: '1px dashed #cbd5e1', borderRadius: 12, textAlign: 'center', color: '#64748b' }}>
          Run Self-Score to grade your drafted proposal. Draft a volume in <strong>Proposal Response</strong> first.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
              {r && <span style={{ background: r.bg, color: r.fg, fontWeight: 700, fontSize: 13, padding: '4px 12px', borderRadius: 999 }}>{r.label}</span>}
              {cov && <span style={{ fontSize: 12, color: '#64748b' }}>Requirements addressed: <strong>{cov.addressed}/{cov.total}</strong></span>}
            </div>
            <p style={{ margin: 0, color: '#1e293b', fontSize: 13, lineHeight: 1.5 }}>{score.readiness_summary}</p>
          </div>

          {Array.isArray(score.factors) && score.factors.length > 0 && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={th}>Requirement</th><th style={th}>Status</th><th style={th}>Note</th>
                </tr></thead>
                <tbody>
                  {score.factors.map((f: any, i: number) => {
                    const s = FACTOR[f.status] || FACTOR.partial;
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #eef2f7' }}>
                        <td style={td}>{f.requirement}</td>
                        <td style={td}><span style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>{(f.status || '').replace('_', ' ')}</span></td>
                        <td style={{ ...td, color: '#475569' }}>{f.note}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <ListCard title="Strengths" items={score.strengths} />
            <ListCard title="Weaknesses" items={score.weaknesses} accent="#b45309" />
            <ListCard title="Gaps to close" items={score.gaps} accent="#991b1b" />
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>Advisory review — verify before submission.</p>
        </div>
      )}
    </div>
  );
}

function ListCard({ title, items, accent }: { title: string; items?: string[]; accent?: string }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ flex: '1 1 260px', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: 'white' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: accent || '#334155', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, color: '#1e293b', fontSize: 13, lineHeight: 1.6 }}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}

const th: React.CSSProperties = { padding: '10px 12px', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 };
const td: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'top' };
